import CheckCircleIcon from '@material-ui/icons/CheckCircleOutlined'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { styled } from 'opensolar-ui'
import { useNotify, useRefresh, useTranslate } from 'ra-core'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import GenericIntegrationAccountDialog from 'resources/externalKeys/GenericIntegrationAccountDialog'
import restClient from 'restClient'
import { AvailableFinanceIntegrationType } from 'types/orgs'
import { RootState } from 'types/state'
import { MANAGE_INTEGRATION_CONFIG_MAP } from '../integrationManagement/constants'
import ManageIntegrationButton from '../integrationManagement/ManageIntegrationButton'
import SetOrgConfigDialog from '../integrationManagement/SetOrgConfigDialog'
import RateChangeLink from '../rateChangeManagement/RateChangeLink'
import FinanceTypeChipsRow from './FinanceTypeChipsRow'
import ImportDialog from './ImportDialog'
import IntegrationBrandedButton from './IntegrationBrandedButton'
import IntegrationPromoImage from './IntegrationPromoImage'
import PromoDialog from './PromoDialog'

const restClientInstance = restClient(window.API_ROOT + '/api')

type PropTypes = {
  integration: AvailableFinanceIntegrationType
  openDialogOnMount?: boolean
}

const PromoCard = styled('div', { name: 'PromoButtonCard' })({
  maxWidth: '100%',
  height: 'fit-content',
  border: '1px solid #EFEFEF',
  boxSizing: 'border-box',
  background: 'rgb(255, 255, 255)',
  borderRadius: 4,
  margin: '10px',
  padding: 15,
  flex: '1 1 0%',
  position: 'relative',
})

const CardContentWrapper = styled('div', { name: 'PromoButtonCardContentWrapper' })({
  height: 35,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})

const ButtonsWrapper = styled('div', { name: 'PromoButtonButtonsWrapper' })({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
})

const IndividualButtonWrapper = styled('div', { name: 'PromoButtonIndividualButtonWrapper' })({
  flex: 1,
  padding: '0px 5px',
})

const RefreshButtonRow = styled('div', { name: 'PromoButtonRefreshButtonRow' })({
  display: 'flex',
  alignItems: 'center',
})

const RefreshButtonWrapper = styled('div', { name: 'PromoButtonRefreshButtonWrapper' })({
  flex: 7,
})

const IconWrapper = styled('div', { name: 'PromoButtonIconWrapper' })({
  position: 'absolute',
  right: '-10px',
  top: '-10px',
})

const PromoButton: React.FC<PropTypes> = (props) => {
  const history = useHistory()
  const location = useLocation()
  const [showDialog, setShowDialog] = useState<boolean>(false)
  const [showImportDialog, setShowImportDialog] = useState<boolean>(false)
  const [showOrgConfigDialog, setShowOrgConfigDialog] = useState<boolean>(false)
  const [showCredentialsDialog, setShowCredentialsDialog] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const org = useSelector(orgSelectors.getOrg)
  const roleId = useSelector(authSelectors.getCurrentRole)?.id
  const isEnabled = useSelector((state: RootState) =>
    authSelectors.getIsGenericIntegrationActive(state, props.integration.integration_name)
  )

  // TODO: pass a flag back from BE so we don't need to hardcode this list
  const isActivationIntegrationOnly = [
    'checkout_finance',
    '28watt',
    'loan_options_ai',
    'figure_lending',
    'hometree',
  ].includes(props.integration?.integration_name) // Includes integrations who doesn't have products but only a flag
  const requiresCredentials =
    props.integration.activation_type === 'api_credentials' &&
    ['hometree', 'cloover'].includes(props.integration?.integration_name)

  const shouldShowActivateButton = isActivationIntegrationOnly || requiresCredentials
  const shouldShowImportButton =
    !isActivationIntegrationOnly &&
    !requiresCredentials &&
    props.integration.activation_type === 'immediately_available'

  const refreshList = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  useEffect(() => {
    if (props.openDialogOnMount) {
      if (isEnabled) {
        notify(
          translate('You are already connected to %{integration_name}!', {
            integration_name: props.integration.display_name,
          }),
          'info'
        )
      }
      setShowDialog(true)
      // Clear the integration param so refresh doesn't re-open, and URL stays clean (handles already-active or no-permission cases)
      const params = new URLSearchParams(location.search)
      if (params.has('integration')) {
        params.delete('integration')
        const newSearch = params.toString()
        history.replace({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' })
      }
    }
  }, [
    props.openDialogOnMount,
    isEnabled,
    props.integration.display_name,
    notify,
    translate,
    history,
    location.pathname,
    location.search,
  ])

  const onRefresh = async (refreshPage: boolean = false) => {
    if (await forceOrgConfigDialog()) {
      setShowOrgConfigDialog(true)
    } else {
      setLoading(true)
      restClientInstance('CUSTOM_POST', 'custom', {
        url: `orgs/${org?.id}/sync_payment_options/${props.integration.integration_name}/`,
        data: { role_id: roleId },
      })
        .then((response) => {
          logAmplitudeEvent('integrated_finance_products_refreshed', {
            integration: props.integration.integration_name,
          })
          if (refreshPage) {
            refreshList()
            const productsAdded = response.data.message.split(' ')[0]
            const activationOnly = response.data?.activation_only
            const partnerDisplayName = encodeURIComponent(props.integration.display_name)
            let newUrl = `${window.location.origin}${window.location.pathname}?products_added=${productsAdded}&partner=${props.integration.integration_name}&partner_display_name=${partnerDisplayName}`
            if (activationOnly) {
              newUrl = `${window.location.origin}${window.location.pathname}?activation_only=${activationOnly}&partner=${props.integration.integration_name}&partner_display_name=${partnerDisplayName}&message=${encodeURIComponent(response.data.message)}`
            }
            window.history.replaceState(null, '', newUrl)
            window.location.reload()
          } else {
            notify(response.data.message, 'success')
            refreshList()
            setShowImportDialog(false)
          }
        })
        .catch((error) => {
          logAmplitudeEvent('integrated_finance_products_refresh_error', {
            integration: props.integration.integration_name,
          })
          notify(error.message, 'warning')
        })
        .finally(() => setLoading(false))
    }
  }

  const forceOrgConfigDialog = async () => {
    if (props.integration?.integration_name === 'mosaic') {
      // Check to see if we have an existing OrgConfig for how Mosaic products should be imported
      const orgConfigResponse = await restClientInstance('CUSTOM_GET', 'custom', {
        url: `orgs/${org?.id}/configs/`,
      })
      // Force the OrgConfig dialog to appear if we haven't yet stored a mosaic_portal_import_type
      return !orgConfigResponse?.data?.org_configs?.mosaic_portal_import_type
    }
    return false
  }

  const onImportProducts = () => {
    // If this integration requires credentials, show the credentials dialog
    if (requiresCredentials && !isEnabled) {
      setShowCredentialsDialog(true)
    } else if (props.integration.banner_content?.import_dialog_content) {
      setShowImportDialog(true)
    } else if (props.integration?.banner_content?.skip_step_3) {
      onRefresh(true)
    } else onRefresh()
  }

  return (
    <PromoCard id={props.integration.integration_name}>
      <CardContentWrapper>
        <IntegrationPromoImage integration={props.integration} />
      </CardContentWrapper>
      <FinanceTypeChipsRow
        supports_commercial={props.integration?.support_commercial}
        supports_residential={props.integration?.support_residential}
      />
      <div style={{ maxWidth: '100%' }}>
        {isEnabled ? (
          <RefreshButtonRow>
            <RefreshButtonWrapper>
              {isActivationIntegrationOnly ? (
                <IntegrationBrandedButton
                  integration={props.integration}
                  label="Learn More"
                  loading={loading}
                  onClick={() => setShowDialog(true)}
                />
              ) : (
                <IntegrationBrandedButton
                  integration={props.integration}
                  label="Refresh Products"
                  loading={loading}
                  onClick={onRefresh}
                />
              )}
            </RefreshButtonWrapper>

            {props.integration?.integration_name === 'sungage' && (
              <ManageIntegrationButton integration="sungage" onSuccess={onRefresh} />
            )}
            {props.integration?.integration_name === 'mosaic' && (
              <ManageIntegrationButton integration="mosaic" onSuccess={onRefresh} />
            )}
          </RefreshButtonRow>
        ) : (
          <ButtonsWrapper>
            {(shouldShowActivateButton || shouldShowImportButton) && (
              <IndividualButtonWrapper>
                <IntegrationBrandedButton
                  integration={props.integration}
                  label={shouldShowActivateButton ? 'Activate' : 'Import Products'}
                  loading={loading}
                  onClick={onImportProducts}
                />
              </IndividualButtonWrapper>
            )}
            <IndividualButtonWrapper>
              <IntegrationBrandedButton
                integration={props.integration}
                label="Learn More"
                loading={loading}
                onClick={() => setShowDialog(true)}
              />
            </IndividualButtonWrapper>
          </ButtonsWrapper>
        )}
      </div>
      {showDialog && (
        <PromoDialog
          integration={props.integration}
          onClose={() => setShowDialog(false)}
          isOpen={showDialog}
          isEnabled={isEnabled}
        />
      )}
      {showImportDialog && (
        <ImportDialog
          integration={props.integration}
          onClose={() => setShowImportDialog(false)}
          onRefresh={onRefresh}
        />
      )}
      {showOrgConfigDialog && (
        <SetOrgConfigDialog
          isOpen={showOrgConfigDialog}
          integrationMap={MANAGE_INTEGRATION_CONFIG_MAP[props.integration?.integration_name]}
          onClose={() => setShowOrgConfigDialog(false)}
          onSuccess={onRefresh}
        />
      )}
      {showCredentialsDialog && (
        <GenericIntegrationAccountDialog
          integrationKey={props.integration.integration_name}
          lenderDisplayName={props.integration.display_name}
          field1Name={(props.integration.banner_content as any)?.field_1_name || 'API Key'}
          field2Name={(props.integration.banner_content as any)?.field_2_name}
          field3Name={(props.integration.banner_content as any)?.field_3_name}
          field4Name={(props.integration.banner_content as any)?.field_4_name}
          activation_type={props.integration.activation_type}
          isOpenExternal={showCredentialsDialog}
          onCloseExternal={() => setShowCredentialsDialog(false)}
          hideButton={true}
        />
      )}
      {isEnabled && (
        <IconWrapper>
          <CheckCircleIcon htmlColor="#ACE167" fontSize="large" />
        </IconWrapper>
      )}
      {isEnabled && (
        <RateChangeLink
          service={{
            name: props.integration.integration_name,
            title: props.integration.display_name,
            logo: props.integration.logo_large_contents,
          }}
        />
      )}
    </PromoCard>
  )
}
export default PromoButton
